import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Lock, Mail } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout } from "../../components/AppShell";
import { Alert, Button, Field, Input } from "../../components/ui";
import { api } from "../../lib/api";
import { authClient, followAuthRedirect } from "../../lib/auth-client";

type Step = "password" | "totp" | "backup";

/** Only allow same-origin, path-only redirects after sign-in. */
function safeNext(next: string | null) {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function LoginPage() {
  const [params] = useSearchParams();
  // Only true on a brand-new installation with a setup code configured.
  const setup = useQuery({ queryKey: ["setup-status"], queryFn: () => api.get<{ available: boolean }>("/api/setup/status"), retry: false, staleTime: 60_000 });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fromApp = params.has("client_id");

  const done = async (data: unknown) => {
    if (followAuthRedirect(data)) return;
    await qc.invalidateQueries();
    navigate(safeNext(params.get("next")), { replace: true });
  };

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const { data, error: err } = await authClient.signIn.email({ email, password });
    setBusy(false);
    if (err) {
      setError(
        err.status === 429
          ? (err.message ?? "Too many attempts. Please wait and try again.")
          : err.code === "EMAIL_NOT_VERIFIED"
            ? "Please verify your email address first. We've sent you a new verification link."
            : "The email or password is incorrect.",
      );
      return;
    }
    if ((data as { twoFactorRedirect?: boolean })?.twoFactorRedirect) {
      setStep("totp");
      setPassword("");
      return;
    }
    await done(data);
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const result =
      step === "totp"
        ? await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, ""), trustDevice })
        : await authClient.twoFactor.verifyBackupCode({ code: code.trim(), trustDevice });
    setBusy(false);
    if (result.error) {
      setError(result.error.status === 429 ? "Too many attempts. Please wait and try again." : "That code didn't work. Check it and try again.");
      setCode("");
      return;
    }
    await done(result.data);
  }

  if (step !== "password") {
    return (
      <AuthLayout
        title="Two-step verification"
        subtitle={step === "totp" ? "Enter the 6-digit code from your authenticator app." : "Enter one of your saved backup codes. Each code works once."}
      >
        <form onSubmit={submitCode} className="space-y-5" noValidate>
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label={step === "totp" ? "Authentication code" : "Backup code"}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode={step === "totp" ? "numeric" : "text"}
                autoComplete="one-time-code"
                pattern={step === "totp" ? "[0-9 ]{6,7}" : undefined}
                maxLength={step === "totp" ? 7 : 24}
                className="h-12 text-center font-mono text-lg tracking-[0.3em]"
                placeholder={step === "totp" ? "000000" : "xxxxx-xxxxx"}
              />
            )}
          </Field>
          <label className="flex items-center gap-2 text-[13px] text-muted">
            <input type="checkbox" className="size-4 accent-[var(--accent)]" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
            Trust this device for 7 days
          </label>
          <Button type="submit" size="lg" className="w-full" loading={busy} icon={<KeyRound className="size-4" />}>
            Verify and continue
          </Button>
          <div className="flex justify-between text-[13px]">
            <button
              type="button"
              className="font-medium text-accent hover:underline"
              onClick={() => {
                setStep(step === "totp" ? "backup" : "totp");
                setCode("");
                setError(null);
              }}
            >
              {step === "totp" ? "Use a backup code instead" : "Use authenticator app"}
            </button>
            <button
              type="button"
              className="text-muted hover:text-fg"
              onClick={() => {
                setStep("password");
                setCode("");
                setError(null);
              }}
            >
              Start over
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Welcome to Galeway" subtitle={fromApp ? "Sign in to continue to your application." : "Sign in to your operations hub."}>
      <form onSubmit={submitPassword} className="space-y-5" noValidate>
        {setup.data?.available && (
          <Alert tone="warning" title="Galeway hasn't been set up yet">
            <Link to="/setup" className="font-medium text-accent hover:underline">
              Run first-time setup
            </Link>{" "}
            to create your company and administrator account.
          </Alert>
        )}
        {params.get("reset") === "1" && <Alert tone="success">Your password has been changed. Sign in with your new password.</Alert>}
        {params.get("invited") === "1" && <Alert tone="success">Your account is ready. Sign in to get started.</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label="Work email">
          {(p) => (
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle" aria-hidden />
              <Input
                {...p}
                type="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 pl-9"
                placeholder="name@company.com"
              />
            </div>
          )}
        </Field>
        <Field label="Password">
          {(p) => (
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-subtle" aria-hidden />
              <Input
                {...p}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 pl-9"
              />
            </div>
          )}
        </Field>
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-[13px] font-medium text-accent hover:underline">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!email || !password}>
          Sign in
        </Button>
        <p className="text-center text-xs leading-relaxed text-subtle">Access is by invitation only. Contact your administrator if you need an account.</p>
      </form>
    </AuthLayout>
  );
}
